import { uiText } from '../utils/i18n';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';
import type { WSOutgoingMessage, FileAttachment, ChatMessage } from '../types';
import { AgentList } from './chat/AgentList';
import { MessageList } from './chat/MessageList';
import { InputBar } from './chat/InputBar';
import { generateId } from './chat/helpers';
import { TasksPanel } from './chat/TasksPanel';
import { SkillsPanel } from './chat/SkillsPanel';
import { MemoryPanel } from './chat/MemoryPanel';
import { ConnectionsPanel } from './chat/ConnectionsPanel';
import { ChatFilesPanel } from './chat/FilesPanel';
import { FilePreview } from './chat/FilePreview';
import { GroupAvatar } from './shared/GroupAvatar';
import { BotAvatar } from './shared/BotAvatar';
import { useT } from '../utils/i18n';
import { getMembers } from '../utils/members-cache';
import styles from './ChatPage.module.css';

type ChatSubTab = 'messages' | 'tasks' | 'skill' | 'memory' | 'connections' | 'files';

interface ChatPageProps {
  send: (msg: WSOutgoingMessage) => void;
  sendBinary: (data: ArrayBuffer | Uint8Array) => void;
}

export function ChatPage({ send, sendBinary }: ChatPageProps) {
  const bots = useStore((s) => s.bots);
  const connected = useStore((s) => s.connected);
  const activeBotName = useStore((s) => s.activeBotName);
  const sessions = useStore((s) => s.sessions);
  const activeSessionId = useStore((s) => s.activeSessionId);

  const t = useT();
  const [activeSubTab, setActiveSubTab] = useState<ChatSubTab>('messages');
  // Track which tabs have been opened at least once — mount on first visit, never unmount
  const [mountedTabs, setMountedTabs] = useState<Set<ChatSubTab>>(new Set(['messages']));
  const handleSubTab = useCallback((tab: ChatSubTab) => {
    setActiveSubTab(tab);
    setMountedTabs(prev => prev.has(tab) ? prev : new Set(prev).add(tab));
  }, []);
  // Reset mounted tabs when switching bot
  useEffect(() => {
    setActiveSubTab('messages');
    setMountedTabs(new Set(['messages']));
  }, [activeBotName]);
  const [previewFile, setPreviewFile] = useState<FileAttachment | null>(null);

  const activeSession = activeSessionId ? sessions.get(activeSessionId) : null;
  const activeBot = bots.find((b) => b.name === activeBotName);
  const isGroupChat = !!activeSession?.groupId;
  const isDm = activeBotName?.startsWith('dm:') ?? false;
  const dmUserId = isDm ? activeBotName!.slice(3) : null;
  const token = useStore((s) => s.token);

  // Workspace members for group chat tooltip
  const [memberNames, setMemberNames] = useState<string[]>([]);
  useEffect(() => {
    if (!isGroupChat || !token) return;
    const { data, promise } = getMembers(token);
    const extract = (map: Map<string, { name?: string }>) => {
      setMemberNames(Array.from(map.values()).map((m) => m.name || '?').filter(Boolean));
    };
    if (data) extract(data); else promise.then(extract);
  }, [isGroupChat, token]);

  // DM partner's avatar/name — use shared cache for instant display
  const [dmMember, setDmMember] = useState<{ name?: string; avatarUrl?: string } | null>(null);
  useEffect(() => {
    if (!isDm || !dmUserId || !token) { setDmMember(null); return; }
    let cancelled = false;
    const { data, promise } = getMembers(token);
    const apply = (map: Map<string, { name?: string; avatarUrl?: string }>) => {
      if (cancelled) return;
      const m = map.get(dmUserId);
      if (m) {
        setDmMember(m);
        if (m.name && activeSessionId) {
          const sess = useStore.getState().sessions.get(activeSessionId);
          if (sess && sess.botName === `dm:${dmUserId}` && (!sess.title || sess.title === 'New Chat' || sess.title !== m.name)) {
            useStore.getState().renameSession(activeSessionId, m.name);
          }
        }
      }
    };
    // If cached, apply immediately (no flash)
    if (data) { apply(data); } else { promise.then(apply); }
    return () => { cancelled = true; };
  }, [isDm, dmUserId, token, activeSessionId]);

  // Restore DM conversation sessions from server on login/connect
  useEffect(() => {
    if (!connected || !token) return;
    let cancelled = false;
    fetch('/auth/dm', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.conversations?.length) return;
        const store = useStore.getState();
        const currentSessions = store.sessions;
        const newSessions = new Map(currentSessions);
        let changed = false;
        for (const conv of data.conversations) {
          const dmKey = `dm:${conv.partnerId}`;
          // Check if a session for this DM partner already exists
          let found = false;
          for (const sess of currentSessions.values()) {
            if (sess.botName === dmKey) { found = true; break; }
          }
          if (!found) {
            const sid = `dm-sess-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const myUserId = localStorage.getItem('novax:userId') || '';
            const initialMessages: ChatMessage[] = [];
            if (conv.lastContent) {
              initialMessages.push({
                id: `dm-preview-${conv.partnerId}`,
                type: conv.lastSenderId === myUserId ? 'user' : 'assistant',
                text: conv.lastContent,
                timestamp: (conv.lastMessageAt || Math.floor(Date.now() / 1000)) * 1000,
              });
            }
            newSessions.set(sid, {
              id: sid,
              botName: dmKey,
              title: conv.partnerName || 'User',
              messages: initialMessages,
              createdAt: (conv.lastMessageAt || Math.floor(Date.now() / 1000)) * 1000,
              updatedAt: (conv.lastMessageAt || Math.floor(Date.now() / 1000)) * 1000,
            });
            changed = true;
          }
        }
        if (changed) {
          try {
            const obj: Record<string, any> = {};
            newSessions.forEach((v, k) => { obj[k] = v; });
            localStorage.setItem('novax:sessions', JSON.stringify(obj));
          } catch { /* storage full */ }
          useStore.setState({ sessions: newSessions });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [connected, token]);

  // Load DM history from server when opening a DM conversation
  useEffect(() => {
    if (!isDm || !dmUserId || !token || !activeSessionId) return;
    let cancelled = false;
    fetch(`/auth/dm/${encodeURIComponent(dmUserId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (cancelled || !data?.messages?.length) return;
        const store = useStore.getState();
        const sess = store.sessions.get(activeSessionId);
        if (!sess || sess.botName !== `dm:${dmUserId}`) return;
        const myUserId = localStorage.getItem('novax:userId') || '';
        const existingIds = new Set(sess.messages.map((m) => m.id));
        const newMessages = data.messages
          .filter((dm: { id: string }) => !existingIds.has(`dm-${dm.id}`))
          .map((dm: { id: string; sender_id: string; content: string; created_at: number }) => ({
            id: `dm-${dm.id}`,
            type: dm.sender_id === myUserId ? 'user' as const : 'assistant' as const,
            text: dm.content,
            timestamp: dm.created_at * 1000,
          }));
        if (newMessages.length > 0) {
          const merged = [...sess.messages, ...newMessages].sort((a, b) => a.timestamp - b.timestamp);
          // Deduplicate by id
          const seen = new Set<string>();
          const deduped = merged.filter((m) => { if (seen.has(m.id)) return false; seen.add(m.id); return true; });
          const sessions = new Map(store.sessions);
          sessions.set(activeSessionId, { ...sess, messages: deduped });
          useStore.setState({ sessions });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isDm, dmUserId, token, activeSessionId]);

  // Get last message status
  const lastMessage = activeSession?.messages[activeSession.messages.length - 1];
  const isThinking = lastMessage?.state?.status === 'thinking' || lastMessage?.state?.status === 'running';

  // For group chats, use session's botName as display; for DMs use member name; for bots, use activeBot
  const chatDisplayName = isGroupChat
    ? (activeSession?.title || '全员群')
    : isDm
      ? (dmMember?.name || (activeSession?.title !== 'New Chat' ? activeSession?.title : '') || '')
      : (activeBot?.name || '');
  const canShowChat = activeSession && (activeBot || isGroupChat || isDm);

  const addMessage = useStore((s) => s.addMessage);
  const handleQuickReply = useCallback((text: string) => {
    if (!activeSession || !activeBotName || !connected || isGroupChat || isDm) return;
    const messageId = generateId();
    addMessage(activeSession.id, { id: `user-${messageId}`, type: 'user', text, timestamp: Date.now() });
    addMessage(activeSession.id, {
      id: messageId, type: 'assistant', text: '', timestamp: Date.now(),
      state: { status: 'thinking', userPrompt: text, responseText: '', toolCalls: [] },
    });
    send({ type: 'chat', botName: activeBotName, chatId: activeSession.id, text, messageId });
  }, [activeSession, activeBotName, connected, isGroupChat, isDm, addMessage, send]);

  return (
    <div className={styles.container}>
      {/* Left: Agent list panel */}
      <div className={styles.sidebar}>
        <AgentList send={send} />
      </div>

      {/* Right: Chat area */}
      <div className={styles.chatArea}>
        {canShowChat ? (
          <>
            {/* Top bar */}
            <div className={styles.topBar}>
              <div className={styles.topBarRow1}>
                <div className={styles.avatarWrap}>
                  {isGroupChat ? (
                    <GroupAvatar color={(() => { try { return localStorage.getItem(`novax:groupColor:${chatDisplayName}`) || undefined; } catch { return undefined; } })()} />
                  ) : isDm ? (
                    dmMember?.avatarUrl ? (
                      <img src={dmMember.avatarUrl} alt="" className={styles.dmAvatarImg} />
                    ) : (
                      <div className={styles.dmAvatarInitial}>{chatDisplayName.charAt(0).toUpperCase()}</div>
                    )
                  ) : (
                    <BotAvatar name={activeBot!.name} size="lg" />
                  )}
                  {!isDm && (
                    <span
                      className={`${styles.statusDot} ${connected ? styles.statusDotOnline : ''}`}
                    />
                  )}
                </div>
                <div className={styles.topBarInfo}>
                  <span className={styles.topBarName}>{chatDisplayName}</span>
                  {isGroupChat && (
                    <div className={styles.topBarMeta}>
                      <span className={styles.hoverTip}>
                        <span className={styles.metaLink}>{bots.length} agents</span>
                        <span className={styles.hoverTipPopup}>
                          <span className={styles.hoverTipTitle}>Agents</span>
                          {bots.map((b) => (
                            <span key={b.name} className={styles.hoverTipItem}>
                              <span className={styles.hoverTipItemDot} />
                              {b.name}
                            </span>
                          ))}
                        </span>
                      </span>
                      {memberNames.length > 0 && (
                        <>
                          <span className={styles.metaSep}>&middot;</span>
                          <span className={styles.hoverTip}>
                            <span className={styles.metaLink}>{memberNames.length} members</span>
                            <span className={styles.hoverTipPopup}>
                              <span className={styles.hoverTipTitle}>Members</span>
                              {memberNames.map((n) => (
                                <span key={n} className={styles.hoverTipItem}>
                                  <span className={styles.hoverTipItemDot} />
                                  {n}
                                </span>
                              ))}
                            </span>
                          </span>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {!isGroupChat && !isDm && (
                <div className={styles.subTabs}>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'messages' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('messages')}
                  >{t('chat.messages')}</button>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'tasks' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('tasks')}
                  >{t('chat.tasks')}</button>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'files' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('files')}
                  >{t('chat.files')}</button>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'skill' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('skill')}
                  >{t('chat.skill')}</button>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'memory' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('memory')}
                  >{t('chat.memory')}</button>
                  <button
                    className={`${styles.subTab} ${activeSubTab === 'connections' ? styles.subTabActive : ''}`}
                    onClick={() => handleSubTab('connections')}
                  >{uiText("连接")}</button>
                </div>
              )}
            </div>

            {previewFile ? (
              <FilePreview file={previewFile} onClose={() => setPreviewFile(null)} />
            ) : (
              <>
                <div style={{ display: (isGroupChat || isDm || activeSubTab === 'messages') ? 'contents' : 'none' }}>
                  <MessageList session={activeSession} bots={bots} onFilePreview={setPreviewFile} dmPartner={isDm ? dmMember : null} onQuickReply={!isGroupChat && !isDm ? handleQuickReply : undefined} />
                  <InputBar
                    send={send}
                    sendBinary={sendBinary}
                    session={activeSession}
                    botName={activeBotName || chatDisplayName}
                    isThinking={isThinking}
                  />
                </div>
                {!isGroupChat && !isDm && activeBot && (
                  <>
                    {mountedTabs.has('tasks') && (
                      <div style={{ display: activeSubTab === 'tasks' ? 'contents' : 'none' }}>
                        <TasksPanel botName={activeBot.name} chatId={activeSession.id} />
                      </div>
                    )}
                    {mountedTabs.has('skill') && (
                      <div style={{ display: activeSubTab === 'skill' ? 'contents' : 'none' }}>
                        <SkillsPanel botName={activeBot.name} />
                      </div>
                    )}
                    {mountedTabs.has('memory') && (
                      <div style={{ display: activeSubTab === 'memory' ? 'contents' : 'none' }}>
                        <MemoryPanel botName={activeBot.name} />
                      </div>
                    )}
                    {mountedTabs.has('files') && (
                      <div style={{ display: activeSubTab === 'files' ? 'contents' : 'none' }}>
                        <ChatFilesPanel messages={activeSession.messages} botName={activeBot.name} onFilePreview={setPreviewFile} />
                      </div>
                    )}
                    {mountedTabs.has('connections') && (
                      <div style={{ display: activeSubTab === 'connections' ? 'contents' : 'none' }}>
                        <ConnectionsPanel botName={activeBot.name} />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </>
        ) : (
          <div className={styles.emptyState}>
            <div className={styles.emptyLogo}>NX</div>
            <h2 className={styles.emptyTitle}>NovaX</h2>
            <p className={styles.emptyText}>
              {bots.length === 0
                ? connected ? t('chat.noAgents') : t('chat.connecting')
                : t('chat.selectAgent')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
